import {
  BaseCurrency,
  getFormatter,
  InstrumentCategoryEnum,
  isDefined,
  Loadable,
  LoadableState,
  useInstrumentStoreItem,
} from '@eih-netbroker/common';
import { useEffect, useMemo } from 'react';
import { useLocale } from '../../../../../context/NbLocalizationContext';
import { NbIndefiniteLoaderValueWrapper } from '../../../../../shared/components/loader-value/NbIndefiniteLoaderValueWrapper';
import { MoneyMaskHOC } from '../../../../../shared/components/money-mask/MoneyMask';
import { instrumentStore } from '../../../../../shared/data/instrumentStore';
import { useInventory } from '../../../../../shared/services/inventory/useInventory';
import { useEStream } from '../../../../../shared/services/sse/useEStream';
import { useAccounts } from '../../../../../shared/services/useAccounts';
import { useOpenPositions } from '../../../services/open-positions/useOpenPositions';
import { OrderFormSourceComponent } from '../../../types/orderTypes';
import { OrderTransactionInfoProps } from './OrderTransactionInfo';
import classes from './OrderTransactionInfo.module.scss';

type QuantityAndPrice = {
  quantity: number;
  price: number | undefined;
};

const unknownValue = '-';

export const OrderTransactionInfoSellInfo = ({ order, sourceComponent }: OrderTransactionInfoProps) => {
  const { translation } = useLocale();
  const { formatCurrency, formatNumber, formatNumberF2, formatInteger } = getFormatter();
  const { subscribeToInstrumentChanges } = useEStream();
  const { selectedAccountOrClient } = useAccounts();
  const { getInventoryCount, getInventoryAveragePrice } = useInventory();
  const { openPositions, refreshOpenPositions } = useOpenPositions();
  const instrumentStoreItem = useInstrumentStoreItem(
    order.instrument ?? null,
    instrumentStore,
    subscribeToInstrumentChanges,
    {
      source: 'OrderTransactionInfoSell',
    },
  );
  const shouldHideFractions = order.instrument?.currency === BaseCurrency;

  const inventoryValue = useMemo<LoadableState<number>>(() => {
    const accountNumber = selectedAccountOrClient.account?.accountNumber;
    const ric = order.instrument?.ric;
    const inventoryCount = ric && accountNumber ? getInventoryCount(ric, accountNumber) : undefined;

    if (!isDefined(order.instrument)) return Loadable.uninitialized;
    if (!isDefined(inventoryCount) || inventoryCount.hasError) return Loadable.failed;
    if (inventoryCount.isLoading) return Loadable.loading;

    if (inventoryCount.isLoaded && order.instrument.instrumentCategory === InstrumentCategoryEnum.Futures) {
      if (Loadable.isUninitialized(openPositions)) {
        return Loadable.loading;
      }

      if (!openPositions.isLoaded) return Loadable.loading;
      const match = openPositions.positions.find(p => p.instrument?.ric === ric);
      return isDefined(match?.pNL) ? Loadable.loaded(inventoryCount.value * match.pNL) : Loadable.loaded(0);
    }

    if (inventoryCount.isLoaded && instrumentStoreItem?.price) {
      return Loadable.loaded(inventoryCount.value * instrumentStoreItem.price.value);
    }

    return Loadable.uninitialized;
  }, [getInventoryCount, selectedAccountOrClient, order.instrument, instrumentStoreItem, openPositions]);

  const quantityAndPrice = useMemo<LoadableState<QuantityAndPrice>>(() => {
    const accountNumber = selectedAccountOrClient.account?.accountNumber;
    const ric = order.instrument?.ric;
    const inventoryCount = ric && accountNumber ? getInventoryCount(ric, accountNumber) : undefined;
    const averagePrice = ric && accountNumber ? getInventoryAveragePrice(ric, accountNumber) : undefined;

    if (!isDefined(order.instrument)) {
      return Loadable.uninitialized;
    }

    if (!isDefined(inventoryCount) || !isDefined(averagePrice) || inventoryCount.hasError || averagePrice.hasError) {
      return Loadable.failed;
    }

    if (inventoryCount.isLoading && averagePrice.isLoading) {
      return Loadable.loading;
    }

    if (inventoryCount.isLoaded && averagePrice.isLoaded) {
      if (inventoryCount.value === 0) {
        return Loadable.uninitialized;
      }

      if (order.instrument.instrumentCategory === InstrumentCategoryEnum.Futures) {
        return Loadable.loaded({ quantity: inventoryCount.value, price: undefined });
      }

      return Loadable.loaded({
        quantity: inventoryCount.value,
        price: averagePrice.value,
      });
    }

    return Loadable.uninitialized;
  }, [getInventoryAveragePrice, getInventoryCount, order.instrument, selectedAccountOrClient.account?.accountNumber]);

  useEffect(() => {
    if (order.instrument?.instrumentCategory !== InstrumentCategoryEnum.Futures) {
      return;
    }

    refreshOpenPositions(selectedAccountOrClient, instrumentStore);
  }, [refreshOpenPositions, selectedAccountOrClient, order.instrument]);

  return (
    <>
      <div className={classes.row}>
        <span
          className={classes.label}
          data-testid="order-transaction-info-inventory-label"
        >
          {translation.orderModal.transactionInfo.inventory}
        </span>
        <MoneyMaskHOC
          isInverse
          size={100}
          bypass={sourceComponent === OrderFormSourceComponent.Modal}
        >
          <NbIndefiniteLoaderValueWrapper
            loadableValue={inventoryValue}
            loadedValueCb={loadedValue => (
              <span
                className={classes.value}
                data-testid="order-transaction-info-inventory-value"
              >
                {loadedValue.value === 0
                  ? unknownValue
                  : formatCurrency(loadedValue.value, order.instrument?.currency ?? BaseCurrency, shouldHideFractions)}
              </span>
            )}
            uninitializedComponent={
              <span
                className={classes.value}
                data-testid="order-transaction-info-inventory-value"
              >
                {unknownValue}
              </span>
            }
          />
        </MoneyMaskHOC>
      </div>
      <div className={classes.row}>
        <div />
        <MoneyMaskHOC
          isInverse
          size={100}
          bypass={sourceComponent === OrderFormSourceComponent.Modal}
        >
          <NbIndefiniteLoaderValueWrapper
            loadableValue={quantityAndPrice}
            loadedValueCb={quantityAndPrice => (
              <div data-testid="order-transaction-info-quantity-and-price-value">
                <span className={classes.value}>
                  {formatNumber(quantityAndPrice.quantity)} {translation.general.piece}
                </span>
                {quantityAndPrice.price !== undefined && (
                  <>
                    <span className={classes.valueSecondary}>{' @ '}</span>
                    {shouldHideFractions ? (
                      <span className={classes.valueSecondary}>{formatInteger(quantityAndPrice.price)}</span>
                    ) : (
                      <span className={classes.valueSecondary}>{formatNumberF2(quantityAndPrice.price)}</span>
                    )}
                  </>
                )}
              </div>
            )}
            uninitializedComponent={<span className={classes.value} />}
            errorComponent={
              <span
                className={classes.value}
                data-testid="order-transaction-info-quantity-and-price-value"
              >
                {unknownValue}
              </span>
            }
          />
        </MoneyMaskHOC>
      </div>
    </>
  );
};
